import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, ArrowLeft, Star, Plus, Check, ShieldCheck, Truck, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import CartDrawer from "../components/site/CartDrawer";

const aud = (n) => `$${Number(n).toFixed(0)}`;

function MiniHeader() {
  const { count, setOpen } = useCart();
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#F8F9FA]/80 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 font-display font-black text-lg tracking-tight uppercase" data-testid="logo"><img src="/logo.png?v=2" alt="Indian TV Box logo" className="w-9 h-9 rounded-lg" /><span>INDIANTV<span className="text-[#FF5722]">.</span>BOX</span></Link>
        <button onClick={() => setOpen(true)} data-testid="open-cart-btn" className="relative p-2">
          <ShoppingCart size={20} />
          {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#FF5722] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
        </button>
      </div>
    </header>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    api.get(`/products`).then((r) => setRelated(r.data)).catch(() => {});
  }, [id]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#F8F9FA]">
      <p className="font-display font-bold text-2xl">Product not found</p>
      <Link to="/" className="text-[#FF5722] font-bold">← Back to store</Link>
    </div>
  );
  if (!product) return <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] font-mono-x text-sm">Loading…</div>;

  const gallery = product.gallery?.length ? product.gallery : [product.image];
  const detail = product.detail || {};
  const addToCart = () => { add({ ref_id: product.id, name: product.name, kind: "product", price: product.price, image: product.image }); toast.success(`${product.name} added`); };
  const buyNow = () => { addToCart(); setOpen(true); };
  const relatedBoxes = related.filter((p) => p.id !== product.id && (p.category || "box") === "box").slice(0, 3);

  return (
    <div className="bg-[#F8F9FA] min-h-screen">
      <MiniHeader />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-24">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[#0F172A] mb-8" data-testid="back-btn"><ArrowLeft size={16} /> Back</button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
              <img src={gallery[active]} alt={product.name} className="w-full h-[420px] sm:h-[520px] object-contain bg-white" data-testid="detail-main-image" />
            </motion.div>
            {gallery.length > 1 && (
              <div className="mt-4 flex gap-3 flex-wrap">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 bg-white ${i === active ? "border-[#FF5722]" : "border-slate-200"}`}>
                    <img src={g} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[#FF5722]">{product.tagline}</p>
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl text-[#0F172A] mt-2 leading-[0.95]" data-testid="detail-title">{product.name}</h1>
            {product.rating > 0 && (
              <div className="flex items-center gap-1 mt-3">
                {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={16} className={s <= Math.round(product.rating) ? "fill-[#FF5722] text-[#FF5722]" : "text-slate-300"} />)}
                <span className="text-sm font-bold text-slate-500 ml-1">{product.rating.toFixed(1)}</span>
              </div>
            )}
            <p className="font-display font-black text-4xl text-[#0F172A] mt-5" data-testid="detail-price">{aud(product.price)}</p>
            <p className="text-base font-medium text-slate-600 mt-4 leading-relaxed">{product.description}</p>

            {product.specs?.length > 0 && (
              <ul className="mt-6 space-y-2">
                {product.specs.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm font-medium text-slate-700"><Check size={16} className="text-[#00C473] mt-0.5 shrink-0" /> {s}</li>)}
              </ul>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={buyNow} data-testid="detail-buy-now" className="bg-[#FF5722] text-white rounded-full px-8 py-4 font-display font-bold uppercase tracking-wide hover:bg-[#0F172A] transition-colors">Buy now</button>
              <button onClick={addToCart} data-testid="detail-add-cart" className="rounded-full px-8 py-4 font-display font-bold uppercase tracking-wide border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-colors flex items-center gap-2"><Plus size={16} /> Add to cart</button>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-bold text-slate-500">
              <span className="flex items-center gap-2"><Truck size={15} className="text-[#4F46E5]" /> Ships from Melbourne</span>
              <span className="flex items-center gap-2"><ShieldCheck size={15} className="text-[#4F46E5]" /> 12-month warranty</span>
            </div>

            {detail.included?.length > 0 && (
              <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-5">
                <p className="flex items-center gap-2 font-display font-bold text-[#0F172A] mb-3"><PackageCheck size={18} className="text-[#4F46E5]" /> What's in the box</p>
                <div className="flex flex-wrap gap-2">
                  {detail.included.map((it, i) => <span key={i} className="text-sm font-medium bg-slate-100 rounded-full px-3 py-1 text-slate-700">{it}</span>)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Description sections */}
        {detail.sections?.length > 0 && (
          <div className="mt-24">
            <h2 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl text-[#0F172A]">Description</h2>
            <div className="mt-10 grid md:grid-cols-2 gap-10">
              {detail.sections.map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                  <h3 className="font-display font-bold text-xl text-[#0F172A] flex items-center gap-3">
                    <span className="text-[#FF5722] font-black">{String(i + 1).padStart(2, "0")}</span>{s.h}
                  </h3>
                  <p className="mt-2 text-base font-medium text-slate-600 leading-relaxed">{s.p}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* FAQs */}
        {detail.faqs?.length > 0 && (
          <div className="mt-24 max-w-3xl">
            <h2 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl text-[#0F172A]">FAQs</h2>
            <Accordion type="single" collapsible className="mt-8" data-testid="detail-faq">
              {detail.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="border-b border-slate-200 py-2">
                  <AccordionTrigger className="font-display font-bold text-base sm:text-lg text-left text-[#0F172A] hover:text-[#4F46E5] hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base font-medium text-slate-600 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* Related */}
        {relatedBoxes.length > 0 && (
          <div className="mt-24">
            <h2 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl text-[#0F172A]">Related products</h2>
            <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 gap-6">
              {relatedBoxes.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="group bg-white border border-slate-200 rounded-3xl overflow-hidden" data-testid={`related-${p.id}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-slate-50">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-bold text-lg text-[#0F172A]">{p.name}</h3>
                    <p className="font-display font-black text-xl text-[#0F172A] mt-1">{aud(p.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      <CartDrawer />
    </div>
  );
}
